import { BASE_URL_STORAGE } from "../../helpers/config";

export const DEFAULT_STORE_SETTING = {
  name: "",
  legal_name: "",
  tagline: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  instagram: "",
  invoice_footer: "",
  qris_payload: "",
  primary_color: "#0B2D32",
  is_multi_outlet: false,
};

export const DEFAULT_PRIMARY_COLOR = "#0B2D32";

export const normalizeHexColor = (value, fallback = DEFAULT_PRIMARY_COLOR) => {
  const color = String(value || "").trim();

  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return color.toUpperCase();
  }

  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color.slice(1).split("").map((character) => `${character}${character}`).join("")}`.toUpperCase();
  }

  return fallback;
};

const hexToRgb = (hexColor) => {
  const color = normalizeHexColor(hexColor).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(color.slice(offset, offset + 2), 16));
};

const rgbToHex = (rgb) => `#${rgb.map((value) => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

const mixColor = (hexColor, target, amount) => {
  const source = hexToRgb(hexColor);
  return rgbToHex(source.map((value, index) => value + (target[index] - value) * amount));
};

const getRelativeLuminance = (hexColor) => {
  const channels = hexToRgb(hexColor).map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

export const getContrastTextColor = (hexColor) =>
  getRelativeLuminance(normalizeHexColor(hexColor)) > 0.48 ? "#10212B" : "#FFFFFF";

export const resolveAssetUrl = (path, fallback = "") => {
  if (!path) {
    return fallback;
  }

  if (path instanceof File) {
    return URL.createObjectURL(path);
  }

  if (
    typeof path === "string" &&
    (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:") || path.startsWith("data:"))
  ) {
    return path;
  }

  return `${BASE_URL_STORAGE || ""}${path}`;
};

export const normalizeStoreSetting = (setting = {}) => ({
  ...DEFAULT_STORE_SETTING,
  ...setting,
  primary_color: normalizeHexColor(setting?.primary_color),
  is_multi_outlet: Boolean(setting?.is_multi_outlet),
});

export const getStorePrimaryColor = (setting = {}) => normalizeStoreSetting(setting).primary_color;

export const applyStoreTheme = (setting = {}) => {
  if (typeof document === "undefined") {
    return;
  }

  const primary = getStorePrimaryColor(setting);
  const rgb = hexToRgb(primary);
  const luminance = getRelativeLuminance(primary);
  const deepMixAmount = luminance > 0.35 ? 0.82 : luminance > 0.12 ? 0.58 : 0.28;
  const root = document.documentElement;

  root.style.setProperty("--store-primary", primary);
  root.style.setProperty("--store-primary-rgb", rgb.join(", "));
  root.style.setProperty("--store-primary-hover", mixColor(primary, [0, 0, 0], 0.16));
  root.style.setProperty("--store-primary-dark", mixColor(primary, [0, 0, 0], luminance > 0.35 ? 0.58 : 0.28));
  root.style.setProperty("--store-primary-deep", mixColor(primary, [8, 28, 32], deepMixAmount));
  root.style.setProperty("--store-primary-soft", mixColor(primary, [255, 255, 255], 0.89));
  root.style.setProperty("--store-primary-border", mixColor(primary, [255, 255, 255], 0.68));
  root.style.setProperty("--store-primary-bright", mixColor(primary, luminance > 0.45 ? [0, 0, 0] : [255, 255, 255], luminance > 0.45 ? 0.12 : 0.28));
  root.style.setProperty("--store-primary-contrast", getContrastTextColor(primary));
};

export const getStoreName = (setting = {}, fallback = "Toko") => {
  const name = String(normalizeStoreSetting(setting).name || "").trim();
  return name || fallback;
};

export const getStoreReceiptName = (setting = {}, fallback = "Toko") => {
  const store = normalizeStoreSetting(setting);
  const storeName = String(store.name || "").trim();
  const outletName = String(store.active_outlet_name || store.outlet?.name || "").trim();

  if (storeName && outletName && storeName.toLowerCase() !== outletName.toLowerCase()) {
    return `${storeName} · ${outletName}`;
  }

  return outletName || storeName || fallback;
};

export const getStoreLogo = (setting = {}, fallback = "") => {
  const store = normalizeStoreSetting(setting);
  return resolveAssetUrl(store.logo_url || store.logo_path || store.logo, fallback);
};

export const getStoreLoginLogo = (setting = {}, fallback = "") => {
  const store = normalizeStoreSetting(setting);
  return resolveAssetUrl(store.logo_url || store.logo_path || store.logo, fallback);
};

export const getStoreBanner = (setting = {}, fallback = "") => {
  const store = normalizeStoreSetting(setting);
  return resolveAssetUrl(store.banner_url || store.banner_path || store.banner, fallback);
};

export const getStoreQris = (setting = {}, fallback = "") => {
  const store = normalizeStoreSetting(setting);
  return resolveAssetUrl(store.qris_image_url || store.qris_image_path || store.qris_image, fallback);
};

export const getStoreQrisPayload = (setting = {}, fallback = "") => {
  const payload = String(normalizeStoreSetting(setting).qris_payload || "").trim();
  return payload || fallback;
};

export const getStoreInvoiceFooter = (setting = {}) => {
  const store = normalizeStoreSetting(setting);
  return String(store.invoice_footer || store.tagline || "").trim();
};

export const applyStoreDocumentBrand = (setting = {}) => {
  if (typeof document === "undefined") {
    return;
  }

  const storeName = getStoreName(setting, "");
  if (storeName) {
    document.title = `${storeName} · AKAS`;
  }
  applyStoreTheme(setting);

  const logo = getStoreLogo(setting, "");
  document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((link) => {
    if (!link.dataset.defaultBrandHref) {
      link.dataset.defaultBrandHref = link.getAttribute("href") || "";
    }

    link.setAttribute("href", logo || link.dataset.defaultBrandHref);
  });
};
